/* eslint-disable jsx-a11y/label-has-associated-control */
import { useIonRouter } from '@ionic/react'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { FormContainer } from '@apps-common/apps/portal/pages/authenticate/components/FormContainer'
import { ButtonRoundedSm } from '@apps-common/components/elements'
import { InputSM } from '@apps-common/components/elements/form/InputSM'
import { VertSpacerMd } from '@apps-common/components/layout'
import { Spinner } from '@apps-common/components/loader'
import { AUTHENTICATE_OPTION_LOGIN } from '@apps-common/config/authenticate-options'
import { commonIcons } from '@apps-common/config/icons'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { PASSWORD_COMPLEXITY_REGEX } from '@apps-common/config/regex'
import { useAuth } from '@apps-common/contexts'
import { useAuthenticateScreenStore } from '@apps-common/stores'
import { getSafeRedirectPath } from '@apps-common/utils/auth-redirect'
// import { ROUTE_AUTHENTICATE } from '@apps-radionet-portal-root/src/config/public-routes'

type FormInputs = {
  email: string
  password: string
  confirmPassword: string
}

interface IProps {
  isMobile: boolean
  code?: string
}

export const CompleteRegistrationForm: React.FC<IProps> = ({ isMobile, code }) => {
  const { setScreen } = useAuthenticateScreenStore()
  const router = useIonRouter()

  const { verifyCode, resetPassword } = useAuth()
  const [initialized, setInitialized] = useState<boolean>(false)
  const [codeInvalid, setCodeInvalid] = useState<boolean>(false)
  // const [code, setCode] = useState<string>()
  const [resetError, setResetError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const {
    register,
    trigger,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FormInputs>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const passwordValue = watch('password')
  const confirmPasswordValue = watch('confirmPassword')

  useEffect(() => {
    if (passwordValue?.length === 0 && confirmPasswordValue?.length === 0) return
    trigger(['password', 'confirmPassword'])
  }, [passwordValue, confirmPasswordValue])

  const initialize = async () => {
    let email: string | null = null

    if (code) {
      const { message } = await verifyCode(code)

      email = message
    }

    if (email) {
      // code must be valid, proceed
      setValue('email', email, { shouldValidate: true })
    } else {
      // code invalid, proceed with error
      setResetError('Invalid registration code.')
      setCodeInvalid(true)
    }

    setInitialized(true)
  }

  useEffect(() => {
    if (code) {
      setResetError(null)
      setCodeInvalid(false)
      setInitialized(false)
      initialize()
    } else {
      setResetError('Invalid registration code.')
      setCodeInvalid(true)
      setInitialized(true)
    }
  }, [code])

  // useEffect(() => {
  //   const params = new URL(document.location.toString()).searchParams
  //   const qCode = params.get('code')

  //   if (qCode) {
  //     setCode(qCode)
  //   } else {
  //     setResetError('Invalid registration code.')
  //     setCodeInvalid(true)
  //     setInitialized(true)
  //   }
  // }, [])

  const onSubmit = async (values: FormInputs) => {
    const { email, password } = values
    const res = await resetPassword(email, code || '', password)

    if (typeof res === 'string') {
      setResetError(res)
      return
    }

    if (res.message && res.result === false) {
      setResetError(res.message)
      return
    }

    if (res.result) {
      router.push(getSafeRedirectPath(window.location.search, '/sites'), 'root', 'replace')
    }
  }

  const handleOnShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const HandleGoToLogin = () => (
    <button
      type='button'
      className='text-text/70 hover:opacity-80 disabled:opacity-70 disabled:cursor-not-allowed text-[0.85rem] jost-medium flex items-center justify-end'
      disabled={isSubmitting}
      onClick={(e) => {
        e?.stopPropagation()
        setScreen(AUTHENTICATE_OPTION_LOGIN)
        router.push('/authenticate', 'root', 'replace')
      }}
    >
      <commonIcons.arrowRightShort className='rotate-180 text-[1.2em]' /> Back to login
    </button>
  )

  return (
    <FormContainer isMobile={isMobile} className='max-w-[500px] md:min-w-[550px]'>
      <div className='absolute top-0 left-0 text-on-mm-brand bg-gradient-to-br from-mm-brand to-mm-brand-2 w-full flex items-center justify-center p-2'>
        <div className='flex items-center justify-center'>
          <h2 className='text-[1rem] jost-medium'>Complete Registration</h2>
        </div>
      </div>
      {!initialized && (
        <div className='mt-[20px]'>
          <Spinner className='border-mm-brand' />
        </div>
      )}
      {initialized && codeInvalid && (
        <div className='w-full'>
          <div className='text-text text-[0.9rem] text-center mb-4 bg-surface-2 border border-border rounded-xl px-4 py-4'>
            <div className='flex items-center justify-center mb-4'>
              <div className='bg-warning text-on-warning border border-on-warning p-2 rounded-lg w-auto'>
                <commonIcons.warning className='text-[1.5em]' />
              </div>
            </div>
            <span>The link is invalid or expired.</span>
            <br />
            <br />
            <span className='text-muted jost-light'>
              Please request a new registration email or contact support. Alternatively, if you know your account email, you can go back to
              login and use the <span className='jost-medium'>forgot password</span> option to receive a password reset link.
            </span>
          </div>
          <div className='flex flex-col items-end justify-end'>
            <HandleGoToLogin />
          </div>
        </div>
      )}
      {initialized && !codeInvalid && (
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          {resetError && (
            <div>
              <p className='text-center text-[0.9em] text-red-600'>{resetError}</p>
            </div>
          )}
          <div className='my-4 flex flex-col'>
            <InputSM
              type='email'
              placeholder='Enter your email'
              label='Email'
              showLabel
              id='forgot-email'
              disabled
              registration={register('email', { required: FORM_DEFAULT_REQUIRED })}
              errors={errors.email}
            />
            <InputSM
              type={showPassword ? 'text' : 'password'}
              placeholder='Enter new password'
              label='Password'
              id='password'
              showLabel
              className={classNames(!showPassword && passwordValue?.length > 0 && 'font-[Verdana] tracking-wide')}
              disabled={codeInvalid || isSubmitting}
              registration={register('password', {
                validate: (value) => {
                  if (!value || value.length === 0) {
                    return 'Required'
                  }

                  if (!value.match(PASSWORD_COMPLEXITY_REGEX)) {
                    return '8 to 32 characters with an uppercase, lowercase and number'
                  }
                  if (value !== watch('confirmPassword')) {
                    return "Password and confirm password don't match"
                  }

                  return true
                }
              })}
              errors={errors.password}
            >
              <button
                type='button'
                className={classNames(
                  'absolute top-0 right-0 flex items-center h-[37px] px-3',
                  (codeInvalid || isSubmitting) && 'opacity-30'
                )}
                disabled={codeInvalid || isSubmitting}
                onClick={handleOnShowPassword}
              >
                {showPassword && <commonIcons.eyeInvisible className='text-lg' />}
                {!showPassword && <commonIcons.eye className='text-lg' />}
              </button>
            </InputSM>
            <InputSM
              type={showPassword ? 'text' : 'password'}
              placeholder='Confirm new password'
              label='Confirm password'
              id='confirmPassword'
              showLabel
              className={classNames(!showPassword && confirmPasswordValue?.length > 0 && 'font-[Verdana] tracking-wide')}
              disabled={codeInvalid || isSubmitting}
              registration={register('confirmPassword', {
                validate: (value) => {
                  if (!value || value.length === 0) {
                    return 'Required'
                  }

                  if (!value.match(PASSWORD_COMPLEXITY_REGEX)) {
                    return '8 to 32 characters with an uppercase, lowercase and number'
                  }

                  if (value !== watch('password')) {
                    return "Password and confirm password don't match"
                  }

                  return true
                }
              })}
              errors={errors.confirmPassword}
            >
              <button
                type='button'
                className={classNames(
                  'absolute top-0 right-0 flex items-center h-[37px] px-3',
                  (codeInvalid || isSubmitting) && 'opacity-30'
                )}
                disabled={codeInvalid || isSubmitting}
                onClick={handleOnShowPassword}
              >
                {showPassword && <commonIcons.eyeInvisible className='text-lg' />}
                {!showPassword && <commonIcons.eye className='text-lg' />}
              </button>
            </InputSM>
            <div className='flex items-center justify-end'>
              <HandleGoToLogin />
            </div>
          </div>
          <VertSpacerMd />
          <ButtonRoundedSm
            type='submit'
            text='Set Password & Continue'
            loading={codeInvalid || isSubmitting}
            icon={<commonIcons.arrowRight />}
            iconRight
            className='w-full !text-on-mm-brand-2 !bg-gradient-to-r !from-mm-brand !to-mm-brand-2 disabled:opacity-30'
          />
        </form>
      )}
    </FormContainer>
  )
}
